<template>
  <modal
    :name="name"
    overlayTransition=""
    :clickToClose="submitted"
    class="Modal ReferralSelectorModal"
    :class="{'Submitted': submitted}">
    <span
      class="ModalClose"
      @click="closeModal">
      ✕
    </span>
    <h3
      v-if="!submitted"
      v-html="$t('referralSelectorModal.title')"/>
    <template v-if="loading">
      {{ $t('common.loading') }}
    </template>
    <template v-else-if="submitted">
      <div class="SubmittedContent">
        <img
          class="SubmittedIllustration"
          src="@/assets/images/icons/illustration-dining.svg"
          alt="">
        <h2 class="SubmittedHeading">
          {{ $t('referralSelectorModal.thankYouHeading') }}
        </h2>
        <p class="SubmittedSubtitle">
          {{ $t('referralSelectorModal.thankYouSubtitle') }}
        </p>
        <a
          class="Button Button--primary"
          @click="closeModal">
          {{ $t('referralSelectorModal.done') }}
        </a>
      </div>
    </template>
    <template v-else>
      <div class="ModalScrollContent">
        <marketing-channel-option
          v-for="channel in marketingChannelOptions"
          :key="channel.value"
          :marketingChannel="channel"
          :selectedMarketingChannels="selectedMarketingChannels"
          :otherMarketingChannel="otherMarketingChannel"
          @input="selectedMarketingChannels = $event"
          @other-input="otherMarketingChannel = $event"/>
        <div class="SalesRepsBreak">
          <p>
            Sales reps
          </p>
        </div>
        <referral-selector-option
          v-for="rep in salesRepresentatives"
          :key="rep ? `sales_representative_${rep.email || rep.name}` : 'sales_representative_none'"
          v-model="selectedSalesRepresentative"
          :salesRepresentative="rep"
          :selectedSalesRepresentative="selectedSalesRepresentative"/>
      </div>
      <div class="ModalActions">
        <a
          class="Button Button--primary"
          :class="{'disabled': submitting}"
          @click="updateReferredBy">
          {{ $t(`referralSelectorModal.${submitting ? 'submitting' : 'submit'}`) }}
        </a>
      </div>
    </template>
  </modal>
</template>

<script>
import OrdersApi from '@/api/OrdersApi'
import SalesRepresentativesApi from '@/api/SalesRepresentativesApi'

const ReferralSelectorOption = () => import('./ReferralSelectorOption')
const MarketingChannelOption = () => import('./MarketingChannelOption/MarketingChannelOption.vue')

export default {
  name: 'referral-selector-modal',
  components: {
    ReferralSelectorOption,
    MarketingChannelOption
  },
  props: {
    salesOrder: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      name: 'referral-selector-modal',
      loading: false,
      salesRepresentatives: [],
      marketingChannelIds: [
        'online_ads',
        'social_media_posts',
        'online_articles',
        'word_of_mouth',
        'other'
      ],
      selectedMarketingChannels: [],
      otherMarketingChannel: '',
      selectedSalesRepresentative: null,
      submitting: false,
      submitted: false
    }
  },
  computed: {
    marketingChannelOptions() {
      return this.marketingChannelIds.map(id => ({
        value: id,
        name: this.$t(`referralSelectorModal.marketingChannels.${id}`)
      }))
    }
  },
  watch: {
    selectedMarketingChannels(newValue) {
      if (!newValue.includes('other')) {
        this.otherMarketingChannel = ''
      }
    }
  },
  created() {
    this.fetchSalesRepresentatives()
  },
  methods: {
    closeModal() {
      this.$modal.hide(this.name)
    },
    async fetchSalesRepresentatives() {
      this.loading = true
      const result = (await SalesRepresentativesApi.index())
      this.salesRepresentatives = [null, ...result.salesRepresentatives]
      this.loading = false
    },
    buildMarketingChannelPayload() {
      const payload = {}

      this.marketingChannelIds.forEach(id => {
        if (id === 'other') {
          if (this.otherMarketingChannel.trim()) {
            payload.other = this.otherMarketingChannel.trim()
          }
        } else if (this.selectedMarketingChannels.includes(id)) {
          payload[id] = true
        }
      })

      return payload
    },
    async updateReferredBy() {
      if (this.selectedSalesRepresentative) {
        this.submitting = true
        const marketingChannels = this.buildMarketingChannelPayload()
        const result = (await OrdersApi.updateOrderReferredBy(this.salesOrder.orderId, this.selectedSalesRepresentative.email, marketingChannels))
        if (!result.error) {
          this.submitted = true
        }
        this.submitting = false
      } else {
        this.submitted = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/assets/css/base";
@import "~@/assets/css/_shared_variables.sass";
@import "~@/assets/css/_variables.scss";

.ReferralSelectorModal {
  /deep/ .v--modal {
    @extend %scrollbar;
    width: 1200px !important;
    max-width: 90% !important;
    height: 900px !important;
    padding: $space-xl !important;
    top: 50% !important;
    transform: translateY(-50%);

    @media #{$small-only} {
      width: calc(100% - 48px) !important;
      height: calc(100vh - 48px) !important;
      height: calc(100dvh - 48px) !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
      padding: $space-l !important;
      top: 24px !important;
      transform: none;
    }
  }
  &.Submitted /deep/ .v--modal {
    display: flex;
    align-items: center;
    justify-content: center;

    @media #{$small-only} {
      display: block;
      max-width: 360px !important;
      padding: $space-l !important;
    }
  }
  h3 {
    @extend %display_medium;
    font-weight: bold;
    margin-bottom: $space-l;

    @media #{$small-only} {
      flex-shrink: 0;
      padding-bottom: $space-l;
      border-bottom: 1px solid $gray-light;
      margin-bottom: 0;
    }
  }

  .ModalScrollContent {
    @media #{$small-only} {
      flex: 1;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      padding-top: $space-l;

      /deep/ label {
        position: relative;
      }
    }
  }
  .SalesRepsBreak {
    position: relative;
    width: 100%;
    margin: $space-l 0;
    border-top: 1px solid $gray-light;

    p {
      position: absolute;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80px;
      margin: 0;
      background-color: $white;
      font-size: 14px;
      color: $gray-dark;
      text-align: center;
    }
  }

  .SubmittedContent {
    text-align: center;
  }
  .SubmittedIllustration {
    width: 240px;
    height: auto;
    margin: 0 auto $space-l;

    @media #{$small-only} {
      width: 160px;
    }
  }
  .SubmittedHeading {
    font-family: $display-font;
    font-size: 2.5rem;
    line-height: 1.1;
    text-transform: uppercase;
    margin-bottom: $space-xxs;

    @media #{$small-only} {
      font-size: 1.75rem;
    }
  }
  .SubmittedSubtitle {
    font-family: $font-family;
    font-weight: bold;
    font-size: 1.5rem;
    line-height: 1.5;
    margin-bottom: $space-l;

    @media #{$small-only} {
      font-size: 1rem;
    }
  }

  .ModalActions {
    position: absolute;
    bottom: $space-xl;
    right: $space-xl;

    @media #{$small-only} {
      position: static;
      flex-shrink: 0;
      text-align: center;
      padding-top: $space-l;
    }

    .Button {
      &.disabled {
        cursor: not-allowed;
      }
    }
  }
}
</style>
